import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import FileUpload from "components/common/FileUpload";
import SingleSelect from "components/common/SingleSelect";
import { createHotDeskValues } from "components/constants/InitialFormValues";
import { ErrorMessage, FieldArray, Form, Formik } from "formik";
import { CircleX } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Col, Container, Row, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import LineIcon from "assets/images/icons/2Line.svg";
import MultipleSelect from "components/common/MultipleSelect";
import { addHotDeskSchema } from "components/common/Validation";
import FormButtons from "components/common/FormButtons";
import { useDispatch, useSelector } from "react-redux";
import { getCityList } from "store/actions/citySlice";
import { getBranchListByCity } from "store/actions/branchSlice";
import { getAmenityList } from "store/actions/amenitySlice";
import { showAlert } from "store/reducers/alertReducer";
import edit from "assets/images/icons/edit.svg";
import plus from "assets/images/icons/plus.svg";
import CustomError from "components/common/CustomError";
import { getOffersList } from "store/actions/offerSlice";
import {
  addWorkspace,
  getWorkspaceDetails,
  updateWorkspace,
} from "store/actions/workspaceSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import Spinners from "components/common/Spinners";
import { InputSwitch } from "primereact/inputswitch";
import { allMonths, allYears } from "components/constants/Constants";
import Select, { components } from "react-select";
import AddOffer from "components/common/Offer/AddOffer";

const AddHotDesk = ({ mode }) => {
  const { t } = useTranslation();
  document.title = t("Add Hot Desk | Ashjar Space");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [formValues, setFormValues] = useState(createHotDeskValues);

  const { isLoading, loading, workspaceDetails } = useSelector(
    (state) => state.Workspace
  );

  const { citiesList } = useSelector((state) => state.Cities);
  const { branchesByCity } = useSelector((state) => state.Branches);
  const { amenitiesList } = useSelector((state) => state.Amenities);
  const { offersList } = useSelector((state) => state.Offers);
  const [showAddTagModal, setShowAddTagModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerMode, setOfferMode] = useState("add");
  const [selectedOffer, setSelectedOffer] = useState(null);

  useEffect(() => {
    // GET CITY LIST
    const fetchCityList = () => {
      dispatch(getCityList());
    };
    // GET AMENITIES LIST
    const fetchAmenitiesList = () => {
      dispatch(getAmenityList());
    };
    // GET OFFERS LIST
    const fetchOffersList = () => {
      dispatch(getOffersList({ type: "all" }));
    };

    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getWorkspaceDetails(payload));
    };

    fetchCityList();
    fetchAmenitiesList();
    fetchOffersList();

    mode === "edit" && getDetails();
  }, [mode]);

  const fetchBranchesByCity = (cityId) => {
    const payload = {
      cityId,
    };
    dispatch(getBranchListByCity(payload));
  };

  useEffect(() => {
    if (mode === "edit" && workspaceDetails) {
      // Destructure keys from meetingDetails
      const {
        spaceTypeInt,
        spaceType,
        name,
        nameAr,
        description,
        descriptionAr,
        imageUrls,
        cityId,
        branchId,
        officeArea,
        amenities,
        status,
        oneDayPass,
        monthlyPlans,
        yearlyPlans,
        isDayEnabled,
        isMonthlyEnabled,
        isYearlyEnabled,
        totalSeats,
      } = workspaceDetails || {};

      cityId?._id && fetchBranchesByCity(cityId?._id);

      const updatedAmenities = amenities?.map((amenity) => amenity?._id) || [];

      // Update formValues using destructured values
      setFormValues((prevValues) => ({
        ...prevValues,
        spaceTypeInt,
        spaceType,
        name,
        nameAr,
        description,
        descriptionAr,
        imageUrls,
        cityId: cityId?._id,
        branchId: branchId?._id || "",
        officeArea: officeArea,
        amenities: updatedAmenities,
        status,
        oneDayPass: oneDayPass ?? {},
        monthlyPlans: monthlyPlans ?? [],
        yearlyPlans: yearlyPlans ?? [],
        isDayEnabled: isDayEnabled ?? false,
        isMonthlyEnabled: isMonthlyEnabled ?? false,
        isYearlyEnabled: isYearlyEnabled ?? false,
        totalSeats: totalSeats ?? null,
      }));
    }
  }, [mode, workspaceDetails]);

  const handleOnSubmit = (values, { setSubmitting }) => {
    const buttonId = document.activeElement.id;
    // UPDATE STATUS ACCORDING TO ACTION TYPE
    const updatedStatus =
      buttonId === "draftBtn" ? 3 : buttonId === "submitBtn" ? 1 : 1;
    const {
      name,
      nameAr,
      description,
      descriptionAr,
      imageUrls,
      cityId,
      branchId,
      amenities,
      status,
      oneDayPass,
      monthlyPlans,
      yearlyPlans,
      isDayEnabled,
      isMonthlyEnabled,
      isYearlyEnabled,
      totalSeats,
    } = values;

    let data = new FormData();
    data.append("spaceTypeInt", "5");
    data.append("spaceType", "hotDesk");
    data.append("name", name);
    data.append("nameAr", nameAr);
    data.append("description", description);
    data.append("descriptionAr", descriptionAr);
    data.append("isDayEnabled", isDayEnabled);
    data.append("isMonthlyEnabled", isMonthlyEnabled);
    data.append("isYearlyEnabled", isYearlyEnabled);

    const filesToUpload = imageUrls.map((fileObj) => fileObj);
    filesToUpload.forEach((file) => {
      data.append("imageUrls", file);
    });

    data.append("cityId", cityId);
    data.append("branchId", branchId);
    data.append("totalSeats", totalSeats);

    amenities.forEach((amenity, index) => {
      data.append(`amenities[${index}]`, amenity);
    });

    if (!isDayEnabled && mode === "add") {
    } else if (isDayEnabled && oneDayPass) {
      data.append("oneDayPass[name]", oneDayPass.name || "");
      data.append("oneDayPass[nameAr]", oneDayPass.nameAr || "");
      data.append("oneDayPass[price]", oneDayPass.price || 0);
    }

    if (!isMonthlyEnabled && mode === "add") {
      data.append("monthlyPlans[]", []);
    } else {
      monthlyPlans.forEach((plan, index) => {
        data.append(`monthlyPlans[${index}][name]`, plan.name);
        data.append(`monthlyPlans[${index}][nameAr]`, plan.nameAr);
        data.append(`monthlyPlans[${index}][duration]`, plan.duration);
        data.append(`monthlyPlans[${index}][price]`, plan.price);
        data.append(
          `monthlyPlans[${index}][discountPrice]`,
          plan.discountPrice
        );
        data.append(`monthlyPlans[${index}][offerId]`, plan.offerId);

        // handle features array
        plan.features.forEach((feature, fIndex) => {
          data.append(
            `monthlyPlans[${index}][features][${fIndex}][title]`,
            feature.title
          );
          data.append(
            `monthlyPlans[${index}][features][${fIndex}][titleAr]`,
            feature.titleAr
          );
        });
      });
    }
    if (!isYearlyEnabled && mode === "add") {
      data.append("yearlyPlans[]", []);
    } else {
      yearlyPlans.forEach((plan, index) => {
        data.append(`yearlyPlans[${index}][name]`, plan.name);
        data.append(`yearlyPlans[${index}][nameAr]`, plan.nameAr);
        data.append(`yearlyPlans[${index}][duration]`, plan.duration);
        data.append(`yearlyPlans[${index}][price]`, plan.price);
        data.append(`yearlyPlans[${index}][discountPrice]`, plan.discountPrice);
        data.append(`yearlyPlans[${index}][offerId]`, plan.offerId);

        // handle features array
        plan.features.forEach((feature, fIndex) => {
          data.append(
            `yearlyPlans[${index}][features][${fIndex}][title]`,
            feature.title
          );
          data.append(
            `yearlyPlans[${index}][features][${fIndex}][titleAr]`,
            feature.titleAr
          );
        });
      });
    }
    data.append("status", updatedStatus);

    if (mode === "add") {
      dispatch(addWorkspace(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    } else if (mode === "edit") {
      data.append("id", id);
      dispatch(updateWorkspace(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    }
  };

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (index) => setDraggedIndex(index);
  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  const handleDrop = (e, setFieldValue, values, fieldName) => {
    e.preventDefault();

    const newItems = [...values[fieldName]];
    const draggedItem = newItems[draggedIndex];

    newItems.splice(draggedIndex, 1);
    newItems.splice(dragOverIndex, 0, draggedItem);

    setFieldValue(fieldName, newItems);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: 10, // ⬅️ your desired radius
      borderColor: state.isFocused ? "#ececec" : "#ececec",
      boxShadow: "none", // ⬅️ remove blue shadow
      "&:hover": {
        borderColor: "#ececec", // ⬅️ hover color
      },
      padding: "0px 4px",
      minHeight: "40px",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#eaf6ff"
        : state.isFocused
        ? "#f2faff"
        : "white",
      color: "#30322c",
      padding: 10,
    }),
    singleValue: (provided) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  const CustomSingleValue = ({ data, ...props }) => {
    if (!data) return null;

    return (
      <components.SingleValue {...props}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {data?.icon && (
            <data.icon
              width={16}
              height={16}
              fill={data.color}
              color={data.color}
            />
          )}
          {data.label}
        </div>
      </components.SingleValue>
    );
  };

  // Custom dropdown menu
  const CustomMenuList = (props) => {
    const { selectProps } = props;

    const handleClick = (fn) => (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Close the menu manually first
      if (props.selectProps && props.selectProps.setMenuIsOpen) {
        props.selectProps.setMenuIsOpen(false);
      }

      // Open modal with slight delay to avoid conflicts
      setTimeout(() => {
        fn?.();
      }, 0);
    };

    return (
      <div className="offers_dropdown">
        <div className="offers_dropdown_btns">
          <div
            onMouseDown={handleClick(selectProps.onAddNew)}
            className="add_new_btn"
            style={{ pointerEvents: "auto", cursor: "pointer" }}
            onClick={() => handleAdd()}
          >
            <img src={plus} width={15} height={15} alt="" className="me-2" />
            Add Offer
          </div>
        </div>
        <components.MenuList {...props} />
      </div>
    );
  };

  const handleAdd = () => {
    setOfferMode("add");
    setSelectedOffer(null);
    setShowOfferModal(true);
  };

  const CustomOption = (props) => {
    const { data, innerRef, innerProps, selectProps } = props;

    const handleEdit = (e) => {
      console.log("edithere");

      e.preventDefault();
      e.stopPropagation(); // avoid selecting option
      setOfferMode("edit");
      setSelectedOffer(data); // pass the clicked option data
      setShowOfferModal(true);
    };

    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "6px 12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {data.icon && (
            <data.icon
              width={16}
              height={16}
              fill={data.color}
              color={data.color}
            />
          )}
          {data.label}
        </div>

        {/* Edit icon */}
        <span
          onClick={handleEdit}
          style={{ cursor: "pointer", color: "#007bff" }}
        >
          <img src={edit} width={15} height={15} alt="" className="me-2" />
        </span>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content common_page add_workspace_page">
        {loading && mode === "edit" ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle
                title={
                  mode === "add" ? t("Add New Hot Desk") : t("Edit Hot Desk")
                }
              />
            </Row>
            <Formik
              initialValues={formValues}
              validationSchema={addHotDeskSchema}
              enableReinitialize={true}
              onSubmit={handleOnSubmit}
            >
              {({ values, setFieldValue, errors, touched }) => (
                <Form>
                  <Row className="common_wrapper">
                    <Col lg={12}>
                      <FileUpload
                        uploadedFiles={values.imageUrls || []}
                        setUploadedFiles={(files) =>
                          setFieldValue("imageUrls", files)
                        }
                        maxFiles={5}
                        maxFileSize={0.6 * 1024 * 1024}
                        acceptedFileTypes={[
                          "image/png",
                          "image/jpeg",
                          "image/svg+xml",
                        ]}
                        requireSquare={false}
                        requireRectangle={false}
                        // onChange={handleFilesChange}
                        className={
                          touched?.imageUrls && errors.imageUrls
                            ? "invalid"
                            : ""
                        }
                      />
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="name"
                          label={t("English Title")}
                        />
                        <CustomField
                          name="name"
                          type="text"
                          placeholder={t("Enter English Title")}
                          touched={touched}
                          errors={errors}
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="nameAr"
                          label={t("Arabic Title")}
                        />
                        <CustomField
                          name="nameAr"
                          type="text"
                          placeholder={t("Enter Arabic Title")}
                          touched={touched}
                          errors={errors}
                          isArabic={true}
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="description"
                          label={t("English Description")}
                        />
                        <CustomField
                          name="description"
                          placeholder="Enter English Description"
                          touched={touched}
                          errors={errors}
                          as="textarea"
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="descriptionAr"
                          label={t("Arabic Description")}
                        />
                        <CustomField
                          name="descriptionAr"
                          placeholder="Enter Arabic Description"
                          touched={touched}
                          errors={errors}
                          as="textarea"
                          isArabic={true}
                        />
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="bg_white br_14 p_23_30">
                        <h2 className="fs_20 mb-4">{t("Location")}</h2>
                        <Row>
                          <Col lg={4}>
                            <div className="form_field">
                              <CustomLabel htmlFor="cityId" label={t("City")} />
                              <SingleSelect
                                options={citiesList?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                value={values.cityId}
                                placeholder={t("Select City")}
                                handleSingleSelectChange={(e) => {
                                  fetchBranchesByCity(e.value);
                                  setFieldValue("cityId", e.value);
                                  setFieldValue("branchId", null);
                                }}
                                className={`${
                                  errors.cityId && touched.cityId
                                    ? "invalid"
                                    : ""
                                }`}
                                error={errors?.cityId}
                              />
                            </div>
                          </Col>
                          <Col lg={4}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="branchId"
                                label={t("Branch")}
                              />
                              <SingleSelect
                                options={branchesByCity?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                value={values.branchId}
                                placeholder={t("Select Branch")}
                                handleSingleSelectChange={(e) => {
                                  setFieldValue("branchId", e.value);
                                }}
                                className={`${
                                  errors.branchId && touched.branchId
                                    ? "invalid"
                                    : ""
                                }`}
                                error={errors?.branchId}
                              />
                            </div>
                          </Col>

                          <Col lg={4}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="totalSeats"
                                label={t("Total Seats")}
                              />
                              <CustomField
                                name="totalSeats"
                                type="number"
                                placeholder={t("Enter Total Seats")}
                                touched={touched}
                                errors={errors}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Col>

                    {/* ONE DAY PASS */}
                    <Col lg={12} style={{ marginTop: 21 }}>
                      <div className="bg_white br_14 p_23_30">
                        <div className="d-flex align-items-center">
                          <InputSwitch
                            checked={values.isDayEnabled}
                            onChange={(e) => {
                              const newValue = !values.isDayEnabled;
                              setFieldValue("isDayEnabled", newValue);
                            }}
                          />
                          <h2 className="fs_20 ms-2">{t("One Day Pass")}</h2>
                        </div>
                        {typeof errors.oneDayPass === "string" && (
                          <div className="text-danger">{errors.oneDayPass}</div>
                        )}
                        {values.isDayEnabled && (
                          <Row className="m-0">
                            <div className="d-flex mt-4">
                              <div
                                className="time_period_inner position-relative"
                                style={{ flexGrow: 1 }}
                              >
                                <Col
                                  lg={12}
                                  className="align-items-center mb-3"
                                >
                                  <Row>
                                    <Col>
                                      <div className="form_field">
                                        <CustomLabel
                                          htmlFor="oneDayPass.name"
                                          label={t("English Title")}
                                        />
                                        <CustomField
                                          name="oneDayPass.name"
                                          type="text"
                                          placeholder={t("Enter English Title")}
                                          touched={touched}
                                          errors={errors}
                                        />
                                      </div>
                                    </Col>
                                    <Col>
                                      <div className="form_field">
                                        <CustomLabel
                                          htmlFor="oneDayPass.nameAr"
                                          label={t("Arabic Title")}
                                        />
                                        <CustomField
                                          name="oneDayPass.nameAr"
                                          type="text"
                                          placeholder={t("Enter Arabic Title")}
                                          touched={touched}
                                          errors={errors}
                                          className="rtl"
                                        />
                                      </div>
                                    </Col>
                                    <Col>
                                      <div className="form_field">
                                        <CustomLabel
                                          htmlFor="oneDayPass.price"
                                          label={t("Price")}
                                        />
                                        <CustomField
                                          name="oneDayPass.price"
                                          type="number"
                                          placeholder={t("Price")}
                                          touched={touched}
                                          errors={errors}
                                          min="0"
                                        />
                                      </div>
                                    </Col>
                                  </Row>
                                </Col>
                              </div>
                            </div>
                          </Row>
                        )}
                      </div>
                    </Col>

                    {/* MONTHLY PLAN */}
                    <Col lg={12} style={{ marginTop: 21 }}>
                      <div className="bg_white br_14 p_23_30">
                        <div className="d-flex align-items-center">
                          <InputSwitch
                            checked={values.isMonthlyEnabled}
                            onChange={(e) => {
                              const newValue = !values.isMonthlyEnabled;
                              setFieldValue("isMonthlyEnabled", newValue);
                            }}
                          />
                          <h2 className="fs_20 ms-2">{t("Monthly Plan")}</h2>
                        </div>
                        {typeof errors.monthlyPlans === "string" && (
                          <div className="text-danger">
                            {errors.monthlyPlans}
                          </div>
                        )}
                        {values.isMonthlyEnabled && (
                          <Row className="m-0">
                            <FieldArray
                              name="monthlyPlans"
                              render={(arrayHelpers) => (
                                <>
                                  {values?.monthlyPlans?.map(
                                    (monthlyPlan, index) => (
                                      <div
                                        className="d-flex mt-4"
                                        key={index}
                                        draggable
                                        onDragStart={() =>
                                          handleDragStart(index)
                                        }
                                        onDragOver={(e) =>
                                          handleDragOver(e, index)
                                        }
                                        onDrop={(e) =>
                                          handleDrop(
                                            e,
                                            setFieldValue,
                                            values,
                                            "monthlyPlans"
                                          )
                                        }
                                        style={{
                                          backgroundColor:
                                            dragOverIndex === index
                                              ? "#f1f3f5"
                                              : "transparent",
                                          borderRadius: 6,
                                          padding: 8,
                                        }}
                                      >
                                        <img
                                          src={LineIcon}
                                          alt="icon"
                                          width={18}
                                          height={10}
                                          style={{
                                            marginRight: 20,
                                            cursor: "grab",
                                          }}
                                        />
                                        <div
                                          className="time_period_inner position-relative"
                                          style={{ flexGrow: 1 }}
                                        >
                                          <Col
                                            lg={12}
                                            className="align-items-center mb-3"
                                          >
                                            <Row>
                                              <Col>
                                                <div className="form_field">
                                                  <CustomLabel
                                                    htmlFor={`monthlyPlans[${index}].name`}
                                                    label={t("English Title")}
                                                  />
                                                  <CustomField
                                                    name={`monthlyPlans[${index}].name`}
                                                    type="text"
                                                    placeholder={t(
                                                      "Enter English Title"
                                                    )}
                                                    touched={touched}
                                                    errors={errors}
                                                  />
                                                </div>
                                              </Col>
                                              <Col>
                                                <div className="form_field">
                                                  <CustomLabel
                                                    htmlFor={`monthlyPlans[${index}].nameAr`}
                                                    label={t("Arabic Title")}
                                                  />
                                                  <CustomField
                                                    name={`monthlyPlans[${index}].nameAr`}
                                                    type="text"
                                                    placeholder={t(
                                                      "Enter Arabic Title"
                                                    )}
                                                    touched={touched}
                                                    errors={errors}
                                                  />
                                                </div>
                                              </Col>
                                              <Col>
                                                <div className="mb-3 form_field single-select">
                                                  <CustomLabel
                                                    htmlFor={`monthlyPlans[${index}].duration`}
                                                    label={t("Months")}
                                                  />
                                                  <SingleSelect
                                                    options={allMonths}
                                                    value={monthlyPlan.duration}
                                                    placeholder={t(
                                                      "Select Month"
                                                    )}
                                                    handleSingleSelectChange={(
                                                      e
                                                    ) => {
                                                      setFieldValue(
                                                        `monthlyPlans[${index}].duration`,
                                                        e.value
                                                      );
                                                    }}
                                                    className={`${
                                                      touched?.monthlyPlans?.[
                                                        index
                                                      ]?.duration &&
                                                      errors?.monthlyPlans?.[
                                                        index
                                                      ]?.duration
                                                        ? "invalid"
                                                        : ""
                                                    }`}
                                                  />
                                                  <ErrorMessage
                                                    name={`monthlyPlans[${index}].duration`}
                                                    component="div"
                                                    className="text-danger"
                                                  />
                                                </div>
                                              </Col>
                                              <Col>
                                                <div className="form_field">
                                                  <CustomLabel
                                                    htmlFor={`monthlyPlans[${index}].price`}
                                                    label={t("Price")}
                                                  />
                                                  <CustomField
                                                    name={`monthlyPlans[${index}].price`}
                                                    type="number"
                                                    placeholder={t("Price")}
                                                    touched={touched}
                                                    errors={errors}
                                                  />
                                                </div>
                                              </Col>
                                              <Col>
                                                <div className="form_field">
                                                  <CustomLabel
                                                    htmlFor={`monthlyPlans[${index}].discountPrice`}
                                                    label={t("Discount Price")}
                                                  />
                                                  <CustomField
                                                    name={`monthlyPlans[${index}].discountPrice`}
                                                    type="number"
                                                    placeholder={t(
                                                      "Discount Price"
                                                    )}
                                                    touched={touched}
                                                    errors={errors}
                                                  />
                                                </div>
                                              </Col>
                                            </Row>
                                            <Row>
                                              <Col lg={6}>
                                                <div className="mb-3 form_field single-select">
                                                  <CustomLabel
                                                    htmlFor={`monthlyPlans[${index}].offerId`}
                                                    label={t("Offers")}
                                                  />

                                                  <Select
                                                    isClearable
                                                    isSearchable={false}
                                                    styles={customSelectStyles}
                                                    options={offersList?.map(
                                                      (offer) => ({
                                                        value: offer._id,
                                                        label: offer.name,
                                                      })
                                                    )}
                                                    components={{
                                                      SingleValue:
                                                        CustomSingleValue,
                                                      MenuList: CustomMenuList,
                                                      Option: CustomOption,
                                                    }}
                                                    value={
                                                      (offersList || [])
                                                        .map((offer) => ({
                                                          value: offer._id,
                                                          label: offer.name,
                                                        }))
                                                        .find(
                                                          (opt) =>
                                                            opt.value ===
                                                            monthlyPlan.offerId
                                                        ) || null
                                                    }
                                                    onChange={(e) => {
                                                      if (
                                                        e?.value ===
                                                        monthlyPlan.offerId
                                                      ) {
                                                        setFieldValue(
                                                          `monthlyPlans[${index}].offerId`,
                                                          null
                                                        ); // Deselect if clicked again
                                                      } else {
                                                        setFieldValue(
                                                          `monthlyPlans[${index}].offerId`,
                                                          e?.value || null
                                                        );
                                                      }
                                                    }}
                                                    placeholder={t(
                                                      "Add New Offers"
                                                    )}
                                                    classNamePrefix="custom_select"
                                                    onEdit={(offer) => {
                                                      setModalType(
                                                        "EDIT_OFFER"
                                                      );
                                                      setSelectedOffer(offer); // store offer in state
                                                      setShowOfferModal(true);
                                                    }}
                                                  />

                                                  <CustomError
                                                    name={`monthlyPlans[${index}].offerId`}
                                                  />
                                                </div>
                                              </Col>
                                            </Row>

                                            <div className="feature-container">
                                              <Row>
                                                <Col lg={6}>
                                                  <p className="fs_16 mb-2">
                                                    {t("Features")}
                                                  </p>
                                                </Col>
                                                <Col lg={6}>
                                                  <div className="g_end mb-3">
                                                    <button
                                                      type="button"
                                                      className="main_btn"
                                                      onClick={() =>
                                                        arrayHelpers.replace(
                                                          index,
                                                          {
                                                            ...values
                                                              .monthlyPlans[
                                                              index
                                                            ],
                                                            features: [
                                                              ...(values
                                                                .monthlyPlans[
                                                                index
                                                              ].features || []),
                                                              {
                                                                title: "",
                                                                titleAr: "",
                                                              },
                                                            ],
                                                          }
                                                        )
                                                      }
                                                    >
                                                      {t("Add New Feature")}
                                                    </button>
                                                  </div>
                                                </Col>
                                              </Row>
                                              <FieldArray
                                                name={`monthlyPlans[${index}].features`}
                                                render={(featureHelpers) => (
                                                  <>
                                                    {values?.monthlyPlans?.[
                                                      index
                                                    ]?.features?.map(
                                                      (feature, fIndex) => (
                                                        <Row
                                                          key={fIndex}
                                                          className="mb-2"
                                                        >
                                                          <Col>
                                                            <div className="form_field">
                                                              <CustomField
                                                                name={`monthlyPlans[${index}].features[${fIndex}].title`}
                                                                type="text"
                                                                placeholder={t(
                                                                  "Enter New Feature EN"
                                                                )}
                                                                touched={
                                                                  touched
                                                                }
                                                                errors={errors}
                                                              />
                                                            </div>
                                                          </Col>
                                                          <Col>
                                                            <div className="form_field">
                                                              <CustomField
                                                                name={`monthlyPlans[${index}].features[${fIndex}].titleAr`}
                                                                type="text"
                                                                placeholder={t(
                                                                  "Enter New Feature AR"
                                                                )}
                                                                touched={
                                                                  touched
                                                                }
                                                                errors={errors}
                                                                className="rtl"
                                                              />
                                                            </div>
                                                          </Col>
                                                          <Col lg={1}>
                                                            {values
                                                              ?.monthlyPlans?.[
                                                              index
                                                            ]?.features
                                                              ?.length > 1 && (
                                                              <button
                                                                type="button"
                                                                className="transparent_btn text_danger p-0"
                                                                onClick={() =>
                                                                  featureHelpers.remove(
                                                                    fIndex
                                                                  )
                                                                }
                                                              >
                                                                <CircleX />
                                                              </button>
                                                            )}
                                                          </Col>
                                                        </Row>
                                                      )
                                                    )}
                                                  </>
                                                )}
                                              />
                                            </div>
                                          </Col>
                                          <div
                                            className="position-absolute"
                                            style={{
                                              top: 10,
                                              right: 10,
                                            }}
                                          >
                                            {values?.monthlyPlans?.length >
                                              1 && (
                                              <button
                                                type="button"
                                                className="transparent_btn text_danger p-0"
                                                onClick={() =>
                                                  arrayHelpers.remove(index)
                                                }
                                              >
                                                <CircleX />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  )}

                                  <Col lg={12}>
                                    <div className="g_end mt-4">
                                      <button
                                        type="button"
                                        className="main_btn"
                                        onClick={() =>
                                          arrayHelpers.push({
                                            name: "",
                                            nameAr: "",
                                            duration: 1,
                                            price: null,
                                            discountPrice: null,
                                            offerId: null,
                                            features: [
                                              { title: "", titleAr: "" },
                                            ],
                                          })
                                        }
                                      >
                                        {t("Add More")}
                                      </button>
                                    </div>
                                  </Col>
                                </>
                              )}
                            />
                            {/* </Row> */}
                          </Row>
                        )}
                      </div>
                    </Col>
                    {/* YEARLY PLAN */}
                    <Col lg={12} style={{ marginTop: 21 }}>
                      <div className="bg_white br_14 p_23_30">
                        <div className="d-flex align-items-center">
                          <InputSwitch
                            checked={values.isYearlyEnabled}
                            onChange={(e) => {
                              const newValue = !values.isYearlyEnabled;
                              setFieldValue("isYearlyEnabled", newValue);
                            }}
                          />
                          <h2 className="fs_20 ms-2">{t("Yearly Plan")}</h2>
                        </div>
                        {typeof errors.yearlyPlans === "string" && (
                          <div className="text-danger">
                            {errors.yearlyPlans}
                          </div>
                        )}
                        {values.isYearlyEnabled && (
                          <Row className="m-0">
                            <FieldArray
                              name="yearlyPlans"
                              render={(arrayHelpers) => (
                                <>
                                  {values?.yearlyPlans?.map(
                                    (yearlyPlan, index) => (
                                      <div
                                        className="d-flex mt-4"
                                        key={index}
                                        draggable
                                        onDragStart={() =>
                                          handleDragStart(index)
                                        }
                                        onDragOver={(e) =>
                                          handleDragOver(e, index)
                                        }
                                        onDrop={(e) =>
                                          handleDrop(
                                            e,
                                            setFieldValue,
                                            values,
                                            "yearlyPlans"
                                          )
                                        }
                                        style={{
                                          backgroundColor:
                                            dragOverIndex === index
                                              ? "#f1f3f5"
                                              : "transparent",
                                          borderRadius: 6,
                                          padding: 8,
                                        }}
                                      >
                                        <img
                                          src={LineIcon}
                                          alt="icon"
                                          width={18}
                                          height={10}
                                          style={{
                                            marginRight: 20,
                                            cursor: "grab",
                                          }}
                                        />
                                        <div
                                          className="time_period_inner position-relative"
                                          style={{ flexGrow: 1 }}
                                        >
                                          <Col
                                            lg={12}
                                            className="align-items-center mb-3"
                                          >
                                            <Row>
                                              <Col>
                                                <div className="form_field">
                                                  <CustomLabel
                                                    htmlFor={`yearlyPlans[${index}].name`}
                                                    label={t("English Title")}
                                                  />
                                                  <CustomField
                                                    name={`yearlyPlans[${index}].name`}
                                                    type="text"
                                                    placeholder={t(
                                                      "Enter English Title"
                                                    )}
                                                    touched={touched}
                                                    errors={errors}
                                                  />
                                                </div>
                                              </Col>
                                              <Col>
                                                <div className="form_field">
                                                  <CustomLabel
                                                    htmlFor={`yearlyPlans[${index}].nameAr`}
                                                    label={t("Arabic Title")}
                                                  />
                                                  <CustomField
                                                    name={`yearlyPlans[${index}].nameAr`}
                                                    type="text"
                                                    placeholder={t(
                                                      "Enter Arabic Title"
                                                    )}
                                                    touched={touched}
                                                    errors={errors}
                                                  />
                                                </div>
                                              </Col>
                                              <Col>
                                                <div className="mb-3 form_field single-select">
                                                  <CustomLabel
                                                    htmlFor={`yearlyPlans[${index}].duration`}
                                                    label={t("Years")}
                                                  />
                                                  <SingleSelect
                                                    options={allYears}
                                                    value={yearlyPlan.duration}
                                                    placeholder={t(
                                                      "Select Year"
                                                    )}
                                                    handleSingleSelectChange={(
                                                      e
                                                    ) => {
                                                      setFieldValue(
                                                        `yearlyPlans[${index}].duration`,
                                                        e.value
                                                      );
                                                    }}
                                                    className={`${
                                                      touched?.yearlyPlan?.[
                                                        index
                                                      ]?.duration &&
                                                      errors?.yearlyPlan?.[
                                                        index
                                                      ]?.duration
                                                        ? "invalid"
                                                        : ""
                                                    }`}
                                                  />
                                                  <ErrorMessage
                                                    name={`yearlyPlans[${index}].duration`}
                                                    component="div"
                                                    className="text-danger"
                                                  />
                                                </div>
                                              </Col>
                                              <Col>
                                                <div className="form_field">
                                                  <CustomLabel
                                                    htmlFor={`yearlyPlans[${index}].price`}
                                                    label={t("Price")}
                                                  />
                                                  <CustomField
                                                    name={`yearlyPlans[${index}].price`}
                                                    type="number"
                                                    placeholder={t("Price")}
                                                    touched={touched}
                                                    errors={errors}
                                                  />
                                                </div>
                                              </Col>
                                              <Col>
                                                <div className="form_field">
                                                  <CustomLabel
                                                    htmlFor={`yearlyPlans[${index}].discountPrice`}
                                                    label={t("Discount Price")}
                                                  />
                                                  <CustomField
                                                    name={`yearlyPlans[${index}].discountPrice`}
                                                    type="number"
                                                    placeholder={t(
                                                      "Discount Price"
                                                    )}
                                                    touched={touched}
                                                    errors={errors}
                                                  />
                                                </div>
                                              </Col>
                                            </Row>
                                            <Row>
                                              <Col lg={6}>
                                                <div className="mb-3 form_field single-select">
                                                  <CustomLabel
                                                    htmlFor={`yearlyPlans[${index}].offerId`}
                                                    label={t("Offers")}
                                                  />

                                                  <Select
                                                    isClearable
                                                    isSearchable={false}
                                                    styles={customSelectStyles}
                                                    options={offersList?.map(
                                                      (offer) => ({
                                                        value: offer._id,
                                                        label: offer.name,
                                                      })
                                                    )}
                                                    components={{
                                                      SingleValue:
                                                        CustomSingleValue,
                                                      MenuList: CustomMenuList,
                                                      Option: CustomOption,
                                                    }}
                                                    value={
                                                      (offersList || [])
                                                        .map((offer) => ({
                                                          value: offer._id,
                                                          label: offer.name,
                                                        }))
                                                        .find(
                                                          (opt) =>
                                                            opt.value ===
                                                            yearlyPlan.offerId
                                                        ) || null
                                                    }
                                                    onChange={(e) => {
                                                      if (
                                                        e?.value ===
                                                        yearlyPlan.offerId
                                                      ) {
                                                        setFieldValue(
                                                          `yearlyPlans[${index}].offerId`,
                                                          null
                                                        ); // Deselect if clicked again
                                                      } else {
                                                        setFieldValue(
                                                          `yearlyPlans[${index}].offerId`,
                                                          e?.value || null
                                                        );
                                                      }
                                                    }}
                                                    placeholder={t(
                                                      "Add New Offers"
                                                    )}
                                                    classNamePrefix="custom_select"
                                                    // 👇 handle add and edit
                                                    onAddNew={() => {
                                                      setModalType("ADD_OFFER");
                                                      setShowOfferModal(true);
                                                    }}
                                                    onEdit={(offer) => {
                                                      setModalType(
                                                        "EDIT_OFFER"
                                                      );
                                                      setSelectedOffer(offer); // store offer in state
                                                      setShowOfferModal(true);
                                                    }}
                                                  />

                                                  <CustomError
                                                    name={`yearlyPlans[${index}].offerId`}
                                                  />
                                                </div>
                                              </Col>
                                            </Row>
                                            <div className="feature-container">
                                              <Row>
                                                <Col lg={6}>
                                                  <p className="fs_16 mb-2">
                                                    {t("Features")}
                                                  </p>
                                                </Col>
                                                <Col lg={6}>
                                                  <div className="g_end mb-3">
                                                    <button
                                                      type="button"
                                                      className="main_btn"
                                                      onClick={() =>
                                                        arrayHelpers.replace(
                                                          index,
                                                          {
                                                            ...values
                                                              .yearlyPlans[
                                                              index
                                                            ],
                                                            features: [
                                                              ...(values
                                                                .yearlyPlans[
                                                                index
                                                              ].features || []),
                                                              {
                                                                title: "",
                                                                titleAr: "",
                                                              },
                                                            ],
                                                          }
                                                        )
                                                      }
                                                    >
                                                      {t("Add New Feature")}
                                                    </button>
                                                  </div>
                                                </Col>
                                              </Row>
                                              <FieldArray
                                                name={`yearlyPlans[${index}].features`}
                                                render={(featureHelpers) => (
                                                  <>
                                                    {values?.yearlyPlans?.[
                                                      index
                                                    ]?.features?.map(
                                                      (feature, fIndex) => (
                                                        <Row
                                                          key={fIndex}
                                                          className="mb-2"
                                                        >
                                                          <Col>
                                                            <div className="form_field">
                                                              <CustomField
                                                                name={`yearlyPlans[${index}].features[${fIndex}].title`}
                                                                type="text"
                                                                placeholder={t(
                                                                  "Enter New Feature EN"
                                                                )}
                                                                touched={
                                                                  touched
                                                                }
                                                                errors={errors}
                                                              />
                                                            </div>
                                                          </Col>
                                                          <Col>
                                                            <div className="form_field">
                                                              <CustomField
                                                                name={`yearlyPlans[${index}].features[${fIndex}].titleAr`}
                                                                type="text"
                                                                placeholder={t(
                                                                  "Enter New Feature AR"
                                                                )}
                                                                touched={
                                                                  touched
                                                                }
                                                                errors={errors}
                                                                className="rtl"
                                                              />
                                                            </div>
                                                          </Col>
                                                          <Col lg={1}>
                                                            {values
                                                              ?.yearlyPlans?.[
                                                              index
                                                            ]?.features
                                                              ?.length > 1 && (
                                                              <button
                                                                type="button"
                                                                className="transparent_btn text_danger p-0"
                                                                onClick={() =>
                                                                  featureHelpers.remove(
                                                                    fIndex
                                                                  )
                                                                }
                                                              >
                                                                <CircleX />
                                                              </button>
                                                            )}
                                                          </Col>
                                                        </Row>
                                                      )
                                                    )}
                                                  </>
                                                )}
                                              />
                                            </div>
                                          </Col>
                                          <div
                                            className="position-absolute"
                                            style={{
                                              top: 10,
                                              right: 10,
                                            }}
                                          >
                                            {values?.yearlyPlans?.length >
                                              1 && (
                                              <button
                                                type="button"
                                                className="transparent_btn text_danger p-0"
                                                onClick={() =>
                                                  arrayHelpers.remove(index)
                                                }
                                              >
                                                <CircleX />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  )}

                                  <Col lg={12}>
                                    <div className="g_end mt-4">
                                      <button
                                        type="button"
                                        className="main_btn"
                                        onClick={() =>
                                          arrayHelpers.push({
                                            name: "",
                                            nameAr: "",
                                            duration: 1,
                                            price: null,
                                            discountPrice: null,
                                            offerId: null,
                                            features: [
                                              { title: "", titleAr: "" },
                                            ],
                                          })
                                        }
                                      >
                                        {t("Add More")}
                                      </button>
                                    </div>
                                  </Col>
                                </>
                              )}
                            />
                            {/* </Row> */}
                          </Row>
                        )}
                      </div>
                    </Col>
                    <Col lg={12} style={{ marginTop: 21 }}>
                      <div className="bg_white br_14 p_23_30">
                        <h2 className="fs_20 mb-4">{t("Amenities")}</h2>
                        <Row>
                          <Col lg={6}>
                            <div className="mb-3 form_field">
                              <CustomLabel
                                htmlFor={`amenities`}
                                label={t("Select Amenities")}
                              />
                              <MultipleSelect
                                options={amenitiesList?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                value={values.amenities}
                                placeholder={t("Select Amenities")}
                                handleMultipleSelectChange={(e) => {
                                  setFieldValue(`amenities`, e);
                                }}
                                className={`${
                                  touched.amenities && errors.amenities
                                    ? "invalid"
                                    : ""
                                }`}
                              />
                              <ErrorMessage
                                name={`amenities`}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>

                  <Row className="px-0 my-4">
                    <FormButtons
                      isDraftBtn={true}
                      isLoading={isLoading}
                      addBtnText={mode === "add" ? t("Add New") : t("Update")}
                    />
                  </Row>
                </Form>
              )}
            </Formik>
          </Container>
        )}
      </div>
      {/* Add Modal */}
      <Modal
        show={showOfferModal}
        onHide={() => setShowOfferModal(false)}
        centered
        className="w_945 w_1115 common_modal"
      >
        <AddOffer
          onClose={() => setShowOfferModal(false)}
          onSuccess={() => dispatch(getOffersList({ type: "all" }))}
          mode={offerMode}
          offer={selectedOffer} // pass selected offer data when editing
          type="office"
        />
      </Modal>
    </React.Fragment>
  );
};

export default AddHotDesk;
